import { useEffect, useState } from "react";
import { Briefcase, MapPin, Plus, Trash2, ExternalLink, Star, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TYPES = ["All", "Full-time", "Contract", "Part-time", "Internship"];
const LOCATIONS = ["All", "Portland", "Beaverton", "Remote", "Hybrid"];
const EMPTY = { title: "", company: "", location: "Portland, OR", job_type: "Full-time", salary: "", url: "", description: "" };

const field =
  "w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900";

export const JobBoard = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("All");
  const [type, setType] = useState("All");
  const [form, setForm] = useState(EMPTY);
  const [posting, setPosting] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (location !== "All") params.set("location", location);
    if (type !== "All") params.set("job_type", type);
    apiFetch(`/jobs?${params.toString()}`).then((r) => setJobs(r.jobs)).catch(() => setJobs([]));
  };

  useEffect(load, [q, location, type]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await apiFetch("/jobs", { method: "POST", body: JSON.stringify(form) });
      toast.success("Listing posted");
      setForm(EMPTY);
      setPosting(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (job) => {
    try {
      await apiFetch(`/jobs/${job.job_id}`, { method: "DELETE" });
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleSponsor = async (job) => {
    try {
      await apiFetch(`/jobs/${job.job_id}/sponsor`, { method: "POST" });
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="job-board">
      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="job-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search roles, companies…"
          className="min-w-[200px] flex-1 rounded-full border border-neutral-300 px-4 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
        />
        <select data-testid="job-location-filter" value={location} onChange={(e) => setLocation(e.target.value)} className="rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold">
          {LOCATIONS.map((l) => (
            <option key={l} value={l}>{l === "All" ? "Any location" : l}</option>
          ))}
        </select>
        <select data-testid="job-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold">
          {TYPES.map((t) => (
            <option key={t} value={t}>{t === "All" ? "Any type" : t}</option>
          ))}
        </select>
        <button
          onClick={() => setPosting((p) => !p)}
          data-testid="post-job-btn"
          className="flex items-center gap-1.5 rounded-full bg-[#B8FF2E] px-4 py-2.5 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
        >
          {posting ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {posting ? "Cancel" : "Post a role"}
        </button>
      </div>

      {posting && (
        <form onSubmit={submit} className="mt-5 grid gap-3 rounded-2xl border border-neutral-200 p-5 sm:grid-cols-2" data-testid="job-form">
          <input required data-testid="job-title" className={field} placeholder="Role title" value={form.title} onChange={set("title")} />
          <input required data-testid="job-company" className={field} placeholder="Company" value={form.company} onChange={set("company")} />
          <input data-testid="job-location" className={field} placeholder="Location (Portland, Remote…)" value={form.location} onChange={set("location")} />
          <select data-testid="job-type" className={field} value={form.job_type} onChange={set("job_type")}>
            {TYPES.filter((t) => t !== "All").map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input data-testid="job-salary" className={field} placeholder="Salary range (optional)" value={form.salary} onChange={set("salary")} />
          <input data-testid="job-url" className={field} placeholder="Application link" value={form.url} onChange={set("url")} />
          <textarea data-testid="job-description" rows={3} className={`${field} sm:col-span-2`} placeholder="What the role involves…" value={form.description} onChange={set("description")} />
          <button data-testid="job-submit" className="w-fit rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-neutral-700">
            Publish listing
          </button>
        </form>
      )}

      <ul className="mt-6 space-y-3" data-testid="job-list">
        {jobs.length === 0 && (
          <li className="rounded-2xl border border-dashed border-neutral-300 p-12 text-center text-sm text-neutral-500">
            No roles match those filters yet.
          </li>
        )}
        {jobs.map((j) => (
          <li
            key={j.job_id}
            data-testid={`job-${j.job_id}`}
            className={`rounded-2xl border p-5 transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.07)] ${
              j.sponsored ? "border-[#84CC16] bg-[#F4FFE0]" : "border-neutral-200"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-bold text-neutral-900">{j.title}</h3>
                  {j.sponsored && (
                    <span className="rounded-full bg-[#B8FF2E] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#14210A]">
                      Sponsored
                    </span>
                  )}
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
                  <span className="flex items-center gap-1 font-semibold">
                    <Briefcase className="h-3.5 w-3.5" /> {j.company}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {j.location}
                  </span>
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold">{j.job_type}</span>
                  {j.salary && <span className="text-xs font-semibold text-neutral-700">{j.salary}</span>}
                </p>
                {j.description && <p className="mt-2 text-sm text-neutral-600">{j.description}</p>}
                <p className="mt-2 text-xs text-neutral-400">Posted by {j.author.name}</p>
              </div>

              <div className="flex items-center gap-2">
                {j.url && (
                  <a
                    href={j.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`apply-${j.job_id}`}
                    className="flex items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700"
                  >
                    Apply <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {user?.role === "admin" && (
                  <button
                    onClick={() => toggleSponsor(j)}
                    data-testid={`sponsor-${j.job_id}`}
                    className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
                    title="Toggle sponsored listing"
                  >
                    <Star className={`h-4 w-4 ${j.sponsored ? "fill-[#84CC16] text-[#4D7C0F]" : "text-neutral-500"}`} />
                  </button>
                )}
                {(j.author.user_id === user?.user_id || user?.role === "admin") && (
                  <button
                    onClick={() => remove(j)}
                    data-testid={`delete-job-${j.job_id}`}
                    className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
                  >
                    <Trash2 className="h-4 w-4 text-[#4D7C0F]" />
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
